import { all, call, put, select, takeLatest } from "redux-saga/effects";
import { request } from "@app/services";

import {
  addSalesPerson,
  addSalesPersonFailed,
  addSalesPersonSuccess,
  assignCustomerToSalesPerson,
  assignCustomerToSalesPersonFailed,
  assignCustomerToSalesPersonSuccess,
  customer,
  customerFailed,
  customerSuccess,
  deleteSalesPerson,
  deleteSalesPersonFailed,
  deleteSalesPersonSuccess,
  salesPerson,
  salesPersonDetails,
  salesPersonDetailsFailed,
  salesPersonDetailsSuccess,
  salesPersonFailed,
  salesPersonSuccess,
  updateSalesPerson,
  updateSalesPersonFailed,
  updateSalesPersonSuccess,
  deleteAssignCustomers,
  deleteAssignCustomersFailed,
  deleteAssignCustomersSuccess,
} from "./slice";
import {
  selectCustomerState,
  selectSalesPersonDetailsState,
  selectSalesPersonState,
} from "./selector";
import * as endpoints from "./endpoints";
import { ISalesPerson } from "./types";

//--------------------GetSalesPersonSagas-------------------------
function* salesPersonSaga(action: ReturnType<typeof salesPerson>): any {
  try {
    const role = action.payload?.role || "Salesperson"; // default fallback
    const response = yield call(
      request.get,
      `${endpoints.salesPerson}/${role}`,
    );

    if (response?.status) {
      yield put(salesPersonSuccess(response.data));
    } else {
      yield put(salesPersonFailed(response.message || "Failed to fetch data"));
    }
  } catch (error: any) {
    yield put(salesPersonFailed(error));
  }
}

//--------------------AddSalesPersonSagas-------------------------

function* addSalesPersonSaga(action: ReturnType<typeof addSalesPerson>): any {
  try {
    const {
      firstName,
      lastName,
      email,
      phone,
      role,
      // assignCustomer,
      profileImage,
    } = action.payload;

    const salesPersons = yield select(selectSalesPersonState);
    const customers = yield select(selectCustomerState);

    const formData = new FormData();
    formData.append("firstName", firstName || "");
    formData.append("lastName", lastName || "");
    formData.append("email", email || "");
    formData.append("phone", String(phone || ""));
    formData.append("role", role || "");

    // ✅ FIXED - Handle assignedCustomers properly
    // if (assignCustomer) {
    //   // Convert to number to ensure consistent type
    //   const customerId =
    //     typeof assignCustomer === "string"
    //       ? parseInt(assignCustomer, 10)
    //       : assignCustomer;

    //   // Backend expects array of objects like [{ id: 36 }]
    //   formData.append(
    //     "assignedCustomers",
    //     JSON.stringify([{ id: customerId }]),
    //   );
    // } else {
    //   // Send empty array if no customer assigned
    //   formData.append("assignedCustomers", JSON.stringify([]));
    // }

    if (profileImage) {
      formData.append("profileImage", profileImage);
    }

    // if (assignCustomer) {
    //   const customerId =
    //     typeof assignCustomer === "string"
    //       ? parseInt(assignCustomer, 10)
    //       : assignCustomer;
    //   const assignedCustomersValue = JSON.stringify([{ id: customerId }]);
    // }

    const response = yield call(
      request.post,
      endpoints.addSalesPerson,
      formData,
      {
        headers: { "Content-Type": "multipart/form-data" },
      },
    );

    if (response?.status) {
      yield put(addSalesPersonSuccess(response?.data));
      const newSalesPerson = [...(salesPersons?.data || []), response.data];
      yield put(salesPersonSuccess(newSalesPerson));

      const newCustomer = {
        id: response.data.id,
        firstName: response.data?.firstName,
        lastName: response.data?.lastName,
        email: response.data?.email,
        phone: response.data?.phone,
        profileImage: response?.data?.profileImage,
      };

      const updatedCustomers = [...(customers?.data || []), newCustomer];

      yield put(customerSuccess(updatedCustomers));
    }
  } catch (error: any) {
    if (error.response) {
      console.error("Response error:", error.response.data);
      console.error("Response status:", error.response.status);
      console.error("Response headers:", error.response.headers);
    }
    yield put(addSalesPersonFailed(error));
  }
}

//--------------------AddSalesPersonSagas-------------------------

function* updateSalesPersonSagas(
  action: ReturnType<typeof updateSalesPerson>,
): any {
  try {
    const salesPersons = yield select(selectSalesPersonState);
    const {
      id,
      firstName,
      lastName,
      email,
      phone,
      role,
      assignCustomer,
      profileImage,
    } = action.payload;

    const formData = new FormData();
    formData.append("firstName", firstName || "");
    formData.append("lastName", lastName || "");
    formData.append("email", email || "");
    formData.append("phone", String(phone || "")); // ensure string
    formData.append("role", role || "");

    if (profileImage) {
      formData.append("profileImage", profileImage); // File object
    }

    const response = yield call(
      request.put,
      `${endpoints.updateSalesPerson}/${id}`,
      formData,
      {
        headers: { "Content-Type": "multipart/form-data" },
      },
    );

    if (response?.status) {
      const updatedUserData = response?.data;
      yield put(updateSalesPersonSuccess(updatedUserData));

      if (Array.isArray(salesPersons?.data) && salesPersons?.data?.length > 0) {
        const updatedSalesPersons = salesPersons.data.map(
          (item: ISalesPerson) =>
            item.id === id ? { ...item, ...updatedUserData } : item,
        );
        yield put(salesPersonSuccess(updatedSalesPersons));
      }
    }
  } catch (error: any) {
    yield put(updateSalesPersonFailed(error));
  }
}

//--------------------DeleteSalesPersonSagas-------------------------

function* deleteSalesPersonSagas(
  action: ReturnType<typeof deleteSalesPerson>,
): any {
  try {
    const { salesPersonId } = action.payload;

    const salesPersons = yield select(selectSalesPersonState);
    const customers = yield select(selectCustomerState);
    const response = yield call(
      request.delete,
      `${endpoints.deleteSalesPerson}/${salesPersonId}`,
    );
    yield put(deleteSalesPersonSuccess(response));
    if (Array.isArray(salesPersons?.data) && salesPersons?.data?.length > 0) {
      const newSalesPersons = salesPersons?.data?.filter(
        (item: { id: number }) => item.id !== salesPersonId,
      );
      yield put(salesPersonSuccess(newSalesPersons));
    }
    if (Array.isArray(customers?.data) && customers?.data?.length > 0) {
      const newCustomers = customers?.data?.filter(
        (item: { id: number }) => item.id !== salesPersonId,
      );
      yield put(customerSuccess(newCustomers));
    }
  } catch (error: any) {
    yield put(deleteSalesPersonFailed(error));
  }
}
//--------------------GetCustomerSagas-------------------------

function* customerSagas(action: ReturnType<typeof customer>): any {
  try {
    const role = action.payload;    
    const response = yield call(request.get, `${endpoints.customer}/${role}`);
    yield put(customerSuccess(response.data));
  } catch (error: any) {
    yield put(customerFailed(error));
  }
}

//--------------------AssignCustomerToSalesPersonSagas-------------------------

function* assignCustomerToSalesPersonSagas(
  action: ReturnType<typeof assignCustomerToSalesPerson>,
): any {
  try {
    const { customerIds, salespersonId } = action.payload;
    const apiUrl = endpoints.assignCustomerToSalesPerson
      .replace("{id}", salespersonId.toString())
      .replace(":id", salespersonId.toString());

    const requestBody = {
      customerIds: customerIds,
    };

    const response = yield call(request.post, apiUrl, requestBody);

    yield put(assignCustomerToSalesPersonSuccess(response?.data));
    yield put(salesPerson({ role: "User" }));
  } catch (error: any) {
    yield put(assignCustomerToSalesPersonFailed(error));
  }
}
//--------------------GetSalesPersonDetailsSagas-------------------------

function* salesPersonDetailsSagas(
  action: ReturnType<typeof salesPersonDetails>,
): any {
  try {
    const { id } = action.payload;
    const response = yield call(
      request.get,
      `${endpoints.salesPersonDetails}/${id}?=${id}`,
    );
    console.log(response, "response sales person details----");
    if (response?.status) {
      yield put(salesPersonDetailsSuccess(response.data));
    }
  } catch (error: any) {
    yield put(salesPersonDetailsFailed(error));
  }
}

// -------------------Delete Customer From SalesPerson ------------------

function* deleteAssignCustomersSagas(
  action: ReturnType<typeof deleteAssignCustomers>,
): any {
  try {
    const { customerId, userId } = action.payload;
    const salesPersonDetails = yield select(selectSalesPersonState);
    console.log(customerId, userId, "customerId, userId");

    const response = yield call(
      request.delete,
      `${endpoints.deleteAssignCustomer}/customerId=${customerId}?userId=${userId}&customerId=${customerId}`,
    );
    if (response?.status) {
      yield put(deleteAssignCustomersSuccess(response?.data));

      const updatedSalesPersonList = salesPersonDetails?.data?.map(
        (salesPerson: any) => {
          if (salesPerson.id === userId) {
            const filteredAssignedCustomers =
              salesPerson.assignedCustomers?.filter(
                (customer: any) => customer.id !== customerId,
              ) || [];

            return {
              ...salesPerson,
              assignedCustomers: filteredAssignedCustomers,
            };
          }
          return salesPerson;
        },
      );
      yield put(salesPersonSuccess(updatedSalesPersonList));
    }
  } catch (err: any) {
    console.log(err, "Error in deleteAssignCustomersSagas----");
    yield put(deleteAssignCustomersFailed(err));
  }
}

function* salesPersonSagas() {
  yield all([
    takeLatest(salesPerson, salesPersonSaga),
    takeLatest(addSalesPerson, addSalesPersonSaga),
    takeLatest(updateSalesPerson, updateSalesPersonSagas),
    takeLatest(deleteSalesPerson, deleteSalesPersonSagas),
    takeLatest(customer, customerSagas),
    takeLatest(assignCustomerToSalesPerson, assignCustomerToSalesPersonSagas),
    takeLatest(salesPersonDetails, salesPersonDetailsSagas),
    takeLatest(deleteAssignCustomers, deleteAssignCustomersSagas),
  ]);
}

export { salesPersonSagas };
